/*
 * The words and the "which view" decisions of the setup steps, as pure functions: one-line summaries a collapsed card
 * shows, copy that depends on what this Hangar serves (Bitrise, Run build), and which body a step renders while its
 * list loads, fails, is empty or has rows. No React, so they can be tested on their own.
 */
import type { ProviderAccount } from '@/api/schemas';
import { STEP_TITLE, stepNumber, type StepId } from './steps';

export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** What a step's list shows: rows still loading, the load error, nothing yet, or the rows. */
export type ListView = 'loading' | 'error' | 'empty' | 'list';

/** A failed or pending load only shows while there is nothing to list (a refetch never hides the rows). */
export function listView(query: { isLoading: boolean; isError: boolean }, data: readonly unknown[] | undefined): ListView {
  if (query.isLoading && !data) return 'loading';
  if (query.isError && !data) return 'error';
  return data && data.length > 0 ? 'list' : 'empty';
}

/** The profiles body: a blocked step shows why, and a list without a project has nothing to show either. */
export function profilesView(blocked: boolean, query: { isLoading: boolean; isError: boolean }, data: readonly unknown[] | undefined, hasProject: boolean): 'blocked' | ListView {
  if (blocked) return 'blocked';
  const view = listView(query, data);
  return view === 'list' && !hasProject ? 'empty' : view;
}

/** The page waits for the projects and the provider accounts; a projects error replaces it. */
export function pageGate(projects: { isError: boolean; isLoading: boolean; data: unknown }, accounts: { isLoading: boolean; data: unknown }): 'error' | 'loading' | null {
  if (projects.isError && !projects.data) return 'error';
  return (projects.isLoading && !projects.data) || (accounts.isLoading && !accounts.data) ? 'loading' : null;
}

type NoteAccount = Pick<ProviderAccount, 'externalAccount' | 'name' | 'status' | 'concurrencyLimit'>;

/** Step 1, collapsed: "acme-expo · 1 slot". Disabled accounts list but do not count their slots. */
export function providersNote(accounts: NoteAccount[]): string {
  if (accounts.length === 0) return 'No build provider yet';
  const slots = accounts.filter((a) => a.status !== 'disabled').reduce((n, a) => n + a.concurrencyLimit, 0);
  return `${accounts.map((a) => a.externalAccount ?? a.name).join(', ')} · ${plural(slots, 'slot', 'slots')}`;
}

/** Step 2, collapsed. */
export const projectsNote = (project: { name: string } | undefined, count: number): string => (project ? `${project.name} · ${plural(count, 'project', 'projects')}` : 'No project yet');

/** Step 3, collapsed: the app slugs, or what is missing. */
export function appsNote(apps: Array<{ slug: string }> | undefined, projectName: string | undefined): string {
  if (apps?.length) return apps.map((a) => a.slug).join(', ');
  return projectName ? `No app in ${projectName} yet` : 'Needs a project';
}

/** Step 4, collapsed: the profile slugs, or what is missing. */
export function profilesNote(profiles: Array<{ slug: string }> | undefined, appName: string | undefined): string {
  if (profiles?.length) return profiles.map((p) => p.slug).join(', ');
  return appName ? `No profile for ${appName} yet` : 'Needs an app';
}

/** Step 5, collapsed: missing required keys first, else how many keys resolve (undefined while unread). */
export function envNote(missing: number, keys: number | undefined): string | undefined {
  if (missing) return `${missing} required ${missing === 1 ? 'key' : 'keys'} missing`;
  return keys === undefined ? undefined : `${keys} ${keys === 1 ? 'key resolves' : 'keys resolve'}`;
}

/** The stepper's warning under the env step: how many required keys are missing, and where when it is one. */
export function envWarning(missing: Array<{ environment: string }>): string | undefined {
  if (missing.length === 0) return undefined;
  const where = missing.length === 1 ? ` in ${missing[0]!.environment}` : '';
  return `${plural(missing.length, 'required key', 'required keys')} missing${where}`;
}

export const appsTitle = (projectName: string | undefined): string => (projectName ? `Apps in ${projectName}` : STEP_TITLE.app);
export const profilesTitle = (appName: string | undefined): string => (appName ? `Build profiles of ${appName}` : STEP_TITLE.profile);

/** Step 3's line: one app per folder; Hangar prepares the build setup, native apps included (SPEC decisions 23 and 30). */
export const appsSummary = (bitrise: boolean): string =>
  bitrise
    ? 'One app per folder of your repository: Hangar prepares how each one is built, native apps included, nothing to set up outside Hangar.'
    : 'One app per folder of your repository: Hangar prepares how it is built, nothing to set up outside Hangar.';

export const appsEmptyDescription = (bitrise: boolean): string =>
  bitrise ? 'Connect an Expo app or a native app: pick its repository and folder, and Hangar prepares its build setup.' : 'Connect the Expo app: pick its repository and folder, and Hangar prepares its build setup.';

/** Why "New profile" is disabled: no app, or no build service account to bind it to. */
export function profilesBlockedReason(hasApp: boolean, providerAccounts: number): string | undefined {
  if (!hasApp) return 'Connect the app first';
  return providerAccounts === 0 ? 'Needs the build service first' : undefined;
}

/** The note of a blocked profiles step: the missing build service, else the missing app. */
export const profilesBlockedNote = (blockedBy: StepId | undefined): string => (blockedBy === 'provider' ? `Needs the build service first (step ${stepNumber('provider')}).` : `Connect the app first (step ${stepNumber('app')}).`);

export const profilesFootnote = (): string => '* The timeout includes the time a build waits in the build queue.';

/** Step 6's line: builds start from the console (CLI and CI builds return with docs/mvp-plan.md P17). No provider named. */
export const buildSummary = (): string => 'Press Run build: Hangar builds the app and this page follows the build live.';

/** Step 1, collapsed, for a client: the build service is ready or on its way (no provider account named, SPEC decision 23). */
export const clientProvidersNote = (accounts: number): string => (accounts > 0 ? 'Build service ready' : 'Build service on its way');

/** The empty builds list says how to start the first one. */
export function buildsEmptyHint(runnable: boolean, runBuild: boolean): string {
  if (!runnable) return `First give a build profile a way to build (step ${stepNumber('profile')}); the build appears here.`;
  return runBuild ? 'Run build starts it; the build appears here.' : 'Run build is not available on this Hangar yet.';
}

/** Where the app folder's files live: the repository root, or its working directory. */
export const folderLocation = (workingDirectory: string | undefined): string => (!workingDirectory || workingDirectory === '.' ? 'at the repository root' : `in ${workingDirectory}`);
